import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { PlusIcon, SearchIcon, UserPlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon, BuildingIcon, PhoneIcon, MailIcon, CalendarIcon, FileTextIcon, UserIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
// Mock data for clients
const mockClients = [{
  id: 1,
  name: 'Gemeente Rotterdam',
  type: 'government',
  contactPerson: 'Jan de Vries',
  email: 'j.devries@rotterdam.nl',
  phone: '010-1234567',
  address: 'Coolsingel 40, 3011 AD Rotterdam',
  status: 'active',
  lastTraining: new Date(2023, 5, 15),
  notes: 'Grote klant met meerdere afdelingen. Voornamelijk geïnteresseerd in sliptrainingen en defensief rijden.'
}, {
  id: 2,
  name: 'Transport BV',
  type: 'business',
  contactPerson: 'Marieke Jansen',
  email: 'm.jansen@transportbv.nl',
  phone: '020-7654321',
  address: 'Logistiekweg 12, 3542 AB Utrecht',
  status: 'active',
  lastTraining: new Date(2023, 5, 20),
  notes: 'Transportbedrijf met 25 vrachtwagenchauffeurs. Code 95 trainingen zijn prioriteit.'
}, {
  id: 3,
  name: 'Zorg Nederland',
  type: 'healthcare',
  contactPerson: 'Peter Bakker',
  email: 'p.bakker@zorgnederland.nl',
  phone: '030-9876543',
  address: 'Zorgplein 5, 3584 CS Utrecht',
  status: 'active',
  lastTraining: new Date(2023, 4, 10),
  notes: 'Zorginstelling met ambulancepersoneel. Interesse in calamiteitentraining en defensief rijden.'
}, {
  id: 4,
  name: 'Logistiek NL',
  type: 'business',
  contactPerson: 'Sophie Visser',
  email: 's.visser@logistieknl.nl',
  phone: '040-1122334',
  address: 'Transportlaan 78, 5652 XR Eindhoven',
  status: 'inactive',
  lastTraining: new Date(2022, 11, 5),
  notes: 'Klant is momenteel inactief. Laatste contact was in december 2022.'
}, {
  id: 5,
  name: 'Gemeente Utrecht',
  type: 'government',
  contactPerson: 'Hans Smit',
  email: 'h.smit@utrecht.nl',
  phone: '030-2233445',
  address: 'Stadsplateau 1, 3521 AZ Utrecht',
  status: 'active',
  lastTraining: new Date(2023, 5, 28),
  notes: 'Regelmatige trainingen voor gemeentelijke voertuigbestuurders.'
}];
// Mock data for training history
const mockTrainingHistory = [{
  id: 1,
  clientId: 1,
  title: 'Sliptraining Basis',
  date: new Date(2023, 5, 15),
  participants: 8,
  instructor: 'Erik de Vries',
  location: 'Rotterdam',
  status: 'completed'
}, {
  id: 2,
  clientId: 1,
  title: 'Defensief Rijden',
  date: new Date(2023, 4, 20),
  participants: 6,
  instructor: 'Lisa Bakker',
  location: 'Rotterdam',
  status: 'completed'
}, {
  id: 3,
  clientId: 2,
  title: 'Code 95 Training',
  date: new Date(2023, 5, 20),
  participants: 12,
  instructor: 'Peter Jansen',
  location: 'Utrecht',
  status: 'completed'
}, {
  id: 4,
  clientId: 2,
  title: 'Sliptraining Gevorderd',
  date: new Date(2023, 5, 10),
  participants: 6,
  instructor: 'Erik de Vries',
  location: 'Rotterdam',
  status: 'completed'
}, {
  id: 5,
  clientId: 3,
  title: 'Calamiteitentraining',
  date: new Date(2023, 4, 10),
  participants: 10,
  instructor: 'Thomas Berg',
  location: 'Amsterdam',
  status: 'completed'
}, {
  id: 6,
  clientId: 5,
  title: 'Sliptraining Basis',
  date: new Date(2023, 5, 28),
  participants: 8,
  instructor: 'Erik de Vries',
  location: 'Utrecht',
  status: 'completed'
}, {
  id: 7,
  clientId: 1,
  title: 'Sliptraining Basis',
  date: new Date(2023, 6, 15),
  participants: 8,
  instructor: 'Erik de Vries',
  location: 'Rotterdam',
  status: 'scheduled'
}];
// Mock data for client categories
const clientCategories = [{
  id: 'all',
  name: 'Alle Klanten'
}, {
  id: 'government',
  name: 'Overheid'
}, {
  id: 'business',
  name: 'Bedrijven'
}, {
  id: 'healthcare',
  name: 'Zorg'
}, {
  id: 'education',
  name: 'Onderwijs'
}];
export function CRM() {
  const [clients, setClients] = useState(mockClients);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'trainings' | 'notes'>('info');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  // New client form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'business',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    status: 'active',
    notes: ''
  });
  // Filter clients based on search, category, and status
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchQuery.toLowerCase()) || client.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()) || client.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || client.type === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || client.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });
  // Get client by ID
  const getClientById = (clientId: number) => {
    return clients.find(client => client.id === clientId) || null;
  };
  // Get training history for a client
  const getClientTrainingHistory = (clientId: number) => {
    return mockTrainingHistory.filter(training => training.clientId === clientId);
  };
  // Select a client
  const selectClient = (clientId: number) => {
    setSelectedClient(clientId);
    setShowClientForm(false);
    setEditMode(false);
  };
  // Start editing a client
  const startEditClient = (clientId: number) => {
    const client = getClientById(clientId);
    if (client) {
      setFormData({
        name: client.name,
        type: client.type,
        contactPerson: client.contactPerson,
        email: client.email,
        phone: client.phone,
        address: client.address,
        status: client.status,
        notes: client.notes
      });
      setSelectedClient(clientId);
      setEditMode(true);
      setShowClientForm(true);
    }
  };
  // Start creating a new client
  const startCreateClient = () => {
    setFormData({
      name: '',
      type: 'business',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      status: 'active',
      notes: ''
    });
    setSelectedClient(null);
    setEditMode(false);
    setShowClientForm(true);
  };
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  // Save client (create or update)
  const saveClient = () => {
    if (editMode && selectedClient) {
      // Update existing client
      setClients(clients.map(client => client.id === selectedClient ? {
        ...client,
        name: formData.name,
        type: formData.type,
        contactPerson: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        status: formData.status as 'active' | 'inactive',
        notes: formData.notes
      } : client));
      showFeedbackMessage('Klant bijgewerkt', 'success');
    } else {
      // Create new client
      const newClient = {
        id: Math.max(0, ...clients.map(c => c.id)) + 1,
        name: formData.name,
        type: formData.type,
        contactPerson: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        status: formData.status as 'active' | 'inactive',
        notes: formData.notes,
        lastTraining: null
      };
      setClients([...clients, newClient]);
      showFeedbackMessage('Nieuwe klant toegevoegd', 'success');
    }
    setShowClientForm(false);
    setEditMode(false);
  };
  // Delete client
  const deleteClient = (clientId: number) => {
    if (window.confirm('Weet je zeker dat je deze klant wilt verwijderen?')) {
      setClients(clients.filter(client => client.id !== clientId));
      if (selectedClient === clientId) {
        setSelectedClient(null);
      }
      showFeedbackMessage('Klant verwijderd', 'success');
    }
  };
  // Show feedback message
  const showFeedbackMessage = (message: string, type: 'success' | 'error') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
    }, 3000);
  };
  // Format date
  const formatDate = (date: Date | null) => {
    if (!date) return 'Nooit';
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  return <div className="space-y-6" data-id="element-306">
      <div className="flex justify-between items-center" data-id="element-307">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-308">Klantbeheer</h1>
        <Button variant="primary" size="sm" onClick={startCreateClient} data-id="element-309">
          <PlusIcon className="h-4 w-4 mr-1" data-id="element-310" />
          Nieuwe Klant
        </Button>
      </div>

      {/* Feedback message */}
      {showFeedback && <div className={`p-3 rounded-md ${feedbackType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-311">
          <div className="flex items-center" data-id="element-312">
            {feedbackType === 'success' ? <CheckIcon className="h-4 w-4 mr-2" data-id="element-313" /> : <XIcon className="h-4 w-4 mr-2" data-id="element-314" />}
            <p data-id="element-315">{feedbackMessage}</p>
          </div>
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-316">
        {/* Client list */}
        <div className="lg:col-span-1" data-id="element-317">
          <div className="mb-4" data-id="element-318">
            <div className="relative" data-id="element-319">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-320">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-321" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" placeholder="Zoeken op naam of email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-322" />
            </div>
          </div>
          <div className="mb-4" data-id="element-323">
            <div className="flex overflow-x-auto space-x-2 pb-2" data-id="element-324">
              {clientCategories.map(category => <Button key={category.id} variant={selectedCategory === category.id ? 'primary' : 'outline'} size="sm" onClick={() => setSelectedCategory(category.id)} className="whitespace-nowrap" data-id="element-325">
                  {category.name}
                </Button>)}
            </div>
          </div>
          <div className="mb-4" data-id="element-326">
            <div className="flex space-x-2" data-id="element-327">
              <Button variant={selectedStatus === 'all' ? 'primary' : 'outline'} size="sm" onClick={() => setSelectedStatus('all')} data-id="element-328">
                Alle
              </Button>
              <Button variant={selectedStatus === 'active' ? 'primary' : 'outline'} size="sm" onClick={() => setSelectedStatus('active')} data-id="element-329">
                Actief
              </Button>
              <Button variant={selectedStatus === 'inactive' ? 'primary' : 'outline'} size="sm" onClick={() => setSelectedStatus('inactive')} data-id="element-330">
                Inactief
              </Button>
            </div>
          </div>
          <Card title={`Klanten (${filteredClients.length})`} data-id="element-331">
            {filteredClients.length > 0 ? <div className="space-y-3 max-h-[600px] overflow-y-auto" data-id="element-332">
                {filteredClients.map(client => <div key={client.id} className={`p-3 border rounded-md cursor-pointer transition-colors ${selectedClient === client.id ? 'border-[#B61C1C] bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => selectClient(client.id)} data-id="element-333">
                    <div className="flex justify-between items-start" data-id="element-334">
                      <div data-id="element-335">
                        <h3 className="font-medium text-gray-900" data-id="element-336">
                          {client.name}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-337">
                          {client.contactPerson}
                        </p>
                      </div>
                      <Badge variant={client.status === 'active' ? 'success' : 'danger'} className="capitalize" data-id="element-338">
                        {client.status === 'active' ? 'Actief' : 'Inactief'}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-600" data-id="element-339">
                      <MailIcon className="h-4 w-4 mr-1" data-id="element-340" />
                      <span className="truncate" data-id="element-341">{client.email}</span>
                    </div>
                    <div className="mt-1 flex justify-between items-center" data-id="element-342">
                      <span className="text-xs text-gray-500" data-id="element-343">
                        Laatste training: {formatDate(client.lastTraining)}
                      </span>
                      <div className="flex space-x-1" data-id="element-344">
                        <button className="text-blue-500 hover:text-blue-700 p-1" onClick={e => {
                    e.stopPropagation();
                    startEditClient(client.id);
                  }} data-id="element-345">
                          <EditIcon className="h-4 w-4" data-id="element-346" />
                        </button>
                        <button className="text-red-500 hover:text-red-700 p-1" onClick={e => {
                    e.stopPropagation();
                    deleteClient(client.id);
                  }} data-id="element-347">
                          <TrashIcon className="h-4 w-4" data-id="element-348" />
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-349">
                Geen klanten gevonden
              </div>}
          </Card>
        </div>

        {/* Client details or form */}
        <div className="lg:col-span-2" data-id="element-350">
          {showClientForm ? <Card title={editMode ? 'Klant bewerken' : 'Nieuwe klant'} data-id="element-351">
              <div className="p-4 space-y-4" data-id="element-352">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-353">
                  <div data-id="element-354">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-355">
                      Naam
                    </label>
                    <input type="text" name="name" className="w-full p-2 border border-gray-300 rounded-md" value={formData.name} onChange={handleInputChange} required data-id="element-356" />
                  </div>
                  <div data-id="element-357">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-358">
                      Type
                    </label>
                    <select name="type" className="w-full p-2 border border-gray-300 rounded-md" value={formData.type} onChange={handleInputChange} data-id="element-359">
                      <option value="business" data-id="element-360">Bedrijf</option>
                      <option value="government" data-id="element-361">Overheid</option>
                      <option value="healthcare" data-id="element-362">Zorg</option>
                      <option value="education" data-id="element-363">Onderwijs</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-364">
                  <div data-id="element-365">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-366">
                      Contactpersoon
                    </label>
                    <input type="text" name="contactPerson" className="w-full p-2 border border-gray-300 rounded-md" value={formData.contactPerson} onChange={handleInputChange} data-id="element-367" />
                  </div>
                  <div data-id="element-368">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-369">
                      Email
                    </label>
                    <input type="email" name="email" className="w-full p-2 border border-gray-300 rounded-md" value={formData.email} onChange={handleInputChange} data-id="element-370" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-371">
                  <div data-id="element-372">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-373">
                      Telefoon
                    </label>
                    <input type="text" name="phone" className="w-full p-2 border border-gray-300 rounded-md" value={formData.phone} onChange={handleInputChange} data-id="element-374" />
                  </div>
                  <div data-id="element-375">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-376">
                      Status
                    </label>
                    <select name="status" className="w-full p-2 border border-gray-300 rounded-md" value={formData.status} onChange={handleInputChange} data-id="element-377">
                      <option value="active" data-id="element-378">Actief</option>
                      <option value="inactive" data-id="element-379">Inactief</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-380">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-381">
                    Adres
                  </label>
                  <input type="text" name="address" className="w-full p-2 border border-gray-300 rounded-md" value={formData.address} onChange={handleInputChange} data-id="element-382" />
                </div>
                <div data-id="element-383">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-384">
                    Notities
                  </label>
                  <textarea name="notes" rows={3} className="w-full p-2 border border-gray-300 rounded-md" value={formData.notes} onChange={handleInputChange} data-id="element-385"></textarea>
                </div>
                <div className="flex justify-end space-x-2 pt-2" data-id="element-386">
                  <Button variant="outline" onClick={() => {
                setShowClientForm(false);
                setEditMode(false);
              }} data-id="element-387">
                    Annuleren
                  </Button>
                  <Button variant="primary" onClick={saveClient} disabled={!formData.name} data-id="element-388">
                    {editMode ? 'Opslaan' : 'Aanmaken'}
                  </Button>
                </div>
              </div>
            </Card> : selectedClient ? <Card title="Klantgegevens" actions={<div className="flex space-x-2" data-id="element-390">
                  <Button variant="outline" size="sm" onClick={() => startEditClient(selectedClient)} data-id="element-391">
                    <EditIcon className="h-4 w-4 mr-1" data-id="element-392" />
                    Bewerken
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-800" onClick={() => deleteClient(selectedClient)} data-id="element-393">
                    <TrashIcon className="h-4 w-4 mr-1" data-id="element-394" />
                    Verwijderen
                  </Button>
                </div>} data-id="element-389">
              {(() => {
            const client = getClientById(selectedClient);
            if (!client) return null;
            return <div data-id="element-395">
                    <div className="border-b border-gray-200" data-id="element-396">
                      <div className="flex space-x-2 mb-4" data-id="element-397">
                        <Button variant={activeTab === 'info' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('info')} data-id="element-398">
                          <UserIcon className="h-4 w-4 mr-1" data-id="element-399" />
                          Informatie
                        </Button>
                        <Button variant={activeTab === 'trainings' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('trainings')} data-id="element-400">
                          <CalendarIcon className="h-4 w-4 mr-1" data-id="element-401" />
                          Trainingen
                        </Button>
                        <Button variant={activeTab === 'notes' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('notes')} data-id="element-402">
                          <FileTextIcon className="h-4 w-4 mr-1" data-id="element-403" />
                          Notities
                        </Button>
                      </div>
                    </div>
                    {activeTab === 'info' && <div className="p-4 space-y-6" data-id="element-404">
                        <div className="flex justify-between items-start" data-id="element-405">
                          <div data-id="element-406">
                            <h2 className="text-xl font-bold text-gray-900" data-id="element-407">
                              {client.name}
                            </h2>
                            <Badge variant={client.status === 'active' ? 'success' : 'danger'} className="mt-1" data-id="element-408">
                              {client.status === 'active' ? 'Actief' : 'Inactief'}
                            </Badge>
                          </div>
                          <div data-id="element-409">
                            <Badge variant="default" data-id="element-410">
                              {client.type === 'business' && 'Bedrijf'}
                              {client.type === 'government' && 'Overheid'}
                              {client.type === 'healthcare' && 'Zorg'}
                              {client.type === 'education' && 'Onderwijs'}
                            </Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-411">
                          <div data-id="element-412">
                            <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-413">
                              Contactpersoon
                            </h3>
                            <p className="text-lg font-medium text-gray-900" data-id="element-414">
                              {client.contactPerson}
                            </p>
                          </div>
                          <div data-id="element-415">
                            <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-416">
                              Laatste Training
                            </h3>
                            <p className="text-lg font-medium text-gray-900" data-id="element-417">
                              {formatDate(client.lastTraining)}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-4" data-id="element-418">
                          <div className="flex items-center" data-id="element-419">
                            <MailIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-420" />
                            <span className="text-gray-800" data-id="element-421">
                              {client.email}
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-422">
                            <PhoneIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-423" />
                            <span className="text-gray-800" data-id="element-424">
                              {client.phone}
                            </span>
                          </div>
                          <div className="flex items-start" data-id="element-425">
                            <BuildingIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-426" />
                            <span className="text-gray-800" data-id="element-427">
                              {client.address}
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-gray-200" data-id="element-428">
                          <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-429">
                            Snelle Acties
                          </h3>
                          <div className="flex flex-wrap gap-2" data-id="element-430">
                            <Button variant="outline" size="sm" data-id="element-431">
                              <CalendarIcon className="h-4 w-4 mr-1" data-id="element-432" />
                              Plan Training
                            </Button>
                            <Button variant="outline" size="sm" data-id="element-433">
                              <MailIcon className="h-4 w-4 mr-1" data-id="element-434" />
                              Stuur Email
                            </Button>
                          </div>
                        </div>
                      </div>}
                    {activeTab === 'trainings' && <div className="p-4" data-id="element-435">
                        <div className="flex justify-between items-center mb-4" data-id="element-436">
                          <h3 className="font-medium text-gray-900" data-id="element-437">
                            Trainingsgeschiedenis
                          </h3>
                          <Button variant="outline" size="sm" data-id="element-438">
                            <PlusIcon className="h-4 w-4 mr-1" data-id="element-439" />
                            Plan Training
                          </Button>
                        </div>
                        {(() => {
                  const trainings = getClientTrainingHistory(selectedClient);
                  const upcomingTrainings = trainings.filter(t => t.status === 'scheduled');
                  const pastTrainings = trainings.filter(t => t.status === 'completed');
                  if (trainings.length === 0) {
                    return <div className="text-center py-8 text-gray-500" data-id="element-440">
                                Geen trainingen gevonden voor deze klant
                              </div>;
                  }
                  return <div className="space-y-6" data-id="element-441">
                              {upcomingTrainings.length > 0 && <div data-id="element-442">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-443">
                                    Geplande Trainingen
                                  </h4>
                                  <div className="space-y-2" data-id="element-444">
                                    {upcomingTrainings.map(training => <div key={training.id} className="p-3 rounded-md border border-blue-200 bg-blue-50" data-id="element-445">
                                        <div className="flex justify-between items-start" data-id="element-446">
                                          <div data-id="element-447">
                                            <div className="font-medium" data-id="element-448">
                                              {training.title}
                                            </div>
                                            <div className="text-sm text-gray-600" data-id="element-449">
                                              {formatDate(training.date)}
                                            </div>
                                          </div>
                                          <Badge variant="info" data-id="element-450">Gepland</Badge>
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600" data-id="element-451">
                                          <div className="flex items-center" data-id="element-452">
                                            <MapPinIcon className="h-4 w-4 mr-1" data-id="element-453" />
                                            <span data-id="element-454">{training.location}</span>
                                          </div>
                                          <div className="flex items-center" data-id="element-455">
                                            <UsersIcon className="h-4 w-4 mr-1" data-id="element-456" />
                                            <span data-id="element-457">
                                              {training.participants} deelnemers
                                            </span>
                                          </div>
                                          <div className="flex items-center" data-id="element-458">
                                            <UserIcon className="h-4 w-4 mr-1" data-id="element-459" />
                                            <span data-id="element-460">{training.instructor}</span>
                                          </div>
                                        </div>
                                      </div>)}
                                  </div>
                                </div>}
                              {pastTrainings.length > 0 && <div data-id="element-461">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-462">
                                    Voltooide Trainingen
                                  </h4>
                                  <div className="space-y-2" data-id="element-463">
                                    {pastTrainings.map(training => <div key={training.id} className="p-3 rounded-md border border-gray-200 bg-gray-50" data-id="element-464">
                                        <div className="flex justify-between items-start" data-id="element-465">
                                          <div data-id="element-466">
                                            <div className="font-medium" data-id="element-467">
                                              {training.title}
                                            </div>
                                            <div className="text-sm text-gray-600" data-id="element-468">
                                              {formatDate(training.date)}
                                            </div>
                                          </div>
                                          <Badge variant="success" data-id="element-469">
                                            Voltooid
                                          </Badge>
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600" data-id="element-470">
                                          <div className="flex items-center" data-id="element-471">
                                            <MapPinIcon className="h-4 w-4 mr-1" data-id="element-472" />
                                            <span data-id="element-473">{training.location}</span>
                                          </div>
                                          <div className="flex items-center" data-id="element-474">
                                            <UsersIcon className="h-4 w-4 mr-1" data-id="element-475" />
                                            <span data-id="element-476">
                                              {training.participants} deelnemers
                                            </span>
                                          </div>
                                          <div className="flex items-center" data-id="element-477">
                                            <UserIcon className="h-4 w-4 mr-1" data-id="element-478" />
                                            <span data-id="element-479">{training.instructor}</span>
                                          </div>
                                        </div>
                                      </div>)}
                                  </div>
                                </div>}
                            </div>;
                })()}
                      </div>}
                    {activeTab === 'notes' && <div className="p-4" data-id="element-480">
                        <div className="mb-4" data-id="element-481">
                          <h3 className="font-medium text-gray-900 mb-2" data-id="element-482">
                            Notities
                          </h3>
                          <textarea rows={6} className="w-full p-3 border border-gray-300 rounded-md" defaultValue={client.notes} placeholder="Voeg notities toe over deze klant..." data-id="element-483"></textarea>
                          <div className="flex justify-end mt-2" data-id="element-484">
                            <Button variant="primary" size="sm" data-id="element-485">
                              <SaveIcon className="h-4 w-4 mr-1" data-id="element-486" />
                              Opslaan
                            </Button>
                          </div>
                        </div>
                      </div>}
                  </div>;
          })()}
            </Card> : <Card data-id="element-487">
              <div className="flex flex-col items-center justify-center py-12" data-id="element-488">
                <UserPlusIcon className="h-12 w-12 text-gray-300" data-id="element-489" />
                <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-490">
                  Geen klant geselecteerd
                </h3>
                <p className="text-gray-500 mb-4" data-id="element-491">
                  Selecteer een klant om details te bekijken of maak een nieuwe
                  klant aan
                </p>
                <Button variant="outline" onClick={startCreateClient} data-id="element-492">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-493" />
                  Nieuwe Klant Aanmaken
                </Button>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}